import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogScrollArea,
  DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Alert, AlertDescription } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import {
  Bookmark,
  CheckCircle,
  Clock,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { BrandIcons } from '../../ui/brand-icons';
import { toast } from 'sonner';
import { AdvancedSocketSelection } from '../AdvancedSocketSelection';
import { integrationRegistry } from '../../../integrations/registry';
import { SpaceSocket } from '../../../services/api';
import { getOrCreateDefaultSocket } from '../../../utils/socketUtils';
import { buildBindingPermissions } from '../shared/bindingPayload';
import {
  xBookmarksService,
  XBookmarkSyncInterval,
  XBookmarksStatus,
} from '../../../services/xBookmarksService';
import { XBookmarksSuccessModalContent } from './XBookmarksSuccessModal';

interface XBookmarksIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateIntegration: (integrationData: unknown) => Promise<{ binding_id: string }>;
  socket?: SpaceSocket;
  spaceId: string;
  existingIntegration?: {
    bindingId: string;
    binding: { binding_id?: string; auth_config?: Record<string, unknown> };
    socket: SpaceSocket;
  };
}

const SYNC_INTERVALS: XBookmarkSyncInterval[] = [5, 15, 60];

const SYNC_INTERVAL_LABEL_KEYS: Record<XBookmarkSyncInterval, string> = {
  5: 'xBookmarksIntegration.syncInterval.options.5',
  15: 'xBookmarksIntegration.syncInterval.options.15',
  60: 'xBookmarksIntegration.syncInterval.options.60',
};

export const XBookmarksIntegrationModal: React.FC<XBookmarksIntegrationModalProps> = ({
  isOpen,
  onClose,
  onCreateIntegration,
  socket,
  spaceId,
  existingIntegration,
}) => {
  const { t } = useTranslation('integrations');
  const [selectedSocket, setSelectedSocket] = useState<SpaceSocket | null>(socket || null);
  const [socketLoading, setSocketLoading] = useState(false);
  const [syncInterval, setSyncInterval] = useState<XBookmarkSyncInterval>(15);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [bindingId, setBindingId] = useState<string | null>(
    existingIntegration?.bindingId ?? null
  );
  const [status, setStatus] = useState<XBookmarksStatus | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (existingIntegration?.bindingId) {
      setBindingId(existingIntegration.bindingId);
      setShowSuccess(true);
      return;
    }
    if (!socket && spaceId) {
      const loadDefaultSocket = async () => {
        setSocketLoading(true);
        try {
          const defaultSocket = await getOrCreateDefaultSocket(
            spaceId,
            integrationRegistry.twitter_bookmarks
          );
          setSelectedSocket(defaultSocket);
        } catch (loadError) {
          console.error('Failed to load socket for X bookmarks:', loadError);
          setError(
            loadError instanceof Error ? loadError.message : 'Failed to load socket'
          );
        } finally {
          setSocketLoading(false);
        }
      };
      loadDefaultSocket();
    } else if (socket) {
      setSelectedSocket(socket);
    }
  }, [isOpen, socket, spaceId, existingIntegration]);

  const loadStatus = useCallback(async (id: string) => {
    try {
      const next = await xBookmarksService.getStatus(id);
      setStatus(next);
    } catch (statusError) {
      console.error('Failed to load X bookmarks status:', statusError);
    }
  }, []);

  useEffect(() => {
    if (!showSuccess || !bindingId) return;
    loadStatus(bindingId);
    const timer = window.setInterval(() => loadStatus(bindingId), 5000);
    return () => window.clearInterval(timer);
  }, [showSuccess, bindingId, loadStatus]);

  const handleConnect = async () => {
    if (!selectedSocket) return;
    setIsConnecting(true);
    setError(null);
    try {
      const permissions = buildBindingPermissions({
        flags: {
          can_read: true,
          can_write: true,
          can_search: true,
          can_delete: false,
        },
        categoriesComma: '',
        excludeTagsComma: '',
        minImportance: 1,
      });

      const integrationData = {
        integration_type: 'twitter_bookmarks',
        integration_name: t('xBookmarksIntegration.defaultName'),
        permissions,
        auth_config: {
          auth_method: 'x_oauth2',
          sync_interval_minutes: syncInterval,
          connected: false,
        },
        socket_id: selectedSocket.socket_id,
      };

      const created = await onCreateIntegration(integrationData);
      const newBindingId = created.binding_id;
      setBindingId(newBindingId);
      await xBookmarksService.prepareBinding(newBindingId, syncInterval);

      const redirectAfter = `${window.location.origin}/integrations?x_connected=${newBindingId}`;
      const authorizeUrl = await xBookmarksService.getConnectUrl(
        newBindingId,
        redirectAfter
      );
      window.location.href = authorizeUrl;
    } catch (connectError) {
      console.error('X bookmarks connect failed:', connectError);
      const message =
        connectError instanceof Error
          ? connectError.message
          : t('xBookmarksIntegration.messages.connectFailed');
      setError(message);
      toast.error(message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleClose = () => {
    setShowSuccess(false);
    setBindingId(null);
    setStatus(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent variant="scrollable" className="max-w-3xl bg-white">
        {showSuccess && bindingId ? (
          <XBookmarksSuccessModalContent
            bindingId={bindingId}
            status={status}
            onRefresh={() => loadStatus(bindingId)}
            onClose={handleClose}
          />
        ) : (
          <>
            <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
              <DialogTitle className="flex items-center gap-3 text-xl">
                <BrandIcons.twitter className="w-8 h-8" />
                {t('xBookmarksIntegration.connectTitle')}
              </DialogTitle>
              <DialogDescription>{t('xBookmarksIntegration.connectDescription')}</DialogDescription>
            </DialogHeader>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogScrollArea className="space-y-6 px-6">
              <AdvancedSocketSelection
                spaceId={spaceId}
                integration={integrationRegistry.twitter_bookmarks}
                currentSocket={selectedSocket}
                onSocketChange={setSelectedSocket}
                disabled={socketLoading}
              />

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {t('xBookmarksIntegration.syncInterval.title')}
                  </CardTitle>
                  <CardDescription>
                    {t('xBookmarksIntegration.syncInterval.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {SYNC_INTERVALS.map((minutes) => (
                    <Button
                      key={minutes}
                      type="button"
                      variant={syncInterval === minutes ? 'default' : 'outline'}
                      onClick={() => setSyncInterval(minutes)}
                    >
                      {t(SYNC_INTERVAL_LABEL_KEYS[minutes])}
                    </Button>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-blue-100 bg-blue-50/40">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Bookmark className="w-4 h-4" />
                    {t('xBookmarksIntegration.oauth.title')}
                  </CardTitle>
                  <CardDescription>{t('xBookmarksIntegration.oauth.description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">bookmark.read</Badge>
                    <Badge variant="secondary">tweet.read</Badge>
                    <Badge variant="secondary">users.read</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('xBookmarksIntegration.oauth.note')}
                  </p>
                </CardContent>
              </Card>

            </DialogScrollArea>
            <DialogFooter variant="scrollable" className="gap-3 px-6 pb-6">
              <Button variant="outline" onClick={handleClose} disabled={isConnecting}>
                {t('common:cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                onClick={handleConnect}
                disabled={!selectedSocket || isConnecting || socketLoading}
              >
                {isConnecting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('xBookmarksIntegration.actions.connecting')}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {t('xBookmarksIntegration.actions.connectX')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
